import { useState, useEffect, useRef } from 'react';
import { getStudyScope, isStudyScopeCurrent } from '@/lib/studyStorage';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { getLessonById } from '@/mocks/lessons';
import { savedSubjects } from '@/mocks/subjects';
import { recordLessonWatch } from '@/mocks/historyStore';
import Pressable from '@/components/base/Pressable';
import LessonPlayer from './components/LessonPlayer';
import LessonMeta from './components/LessonMeta';
import LessonResources from './components/LessonResources';
import AISummary from './components/AISummary';
import PracticeTests from './components/PracticeTests';
import Worksheets from './components/Worksheets';
import HomeworkHelp from './components/HomeworkHelp';
import RelatedVideos from './components/RelatedVideos';
import SummaryNotes from './components/SummaryNotes';
import Flashcards from './components/Flashcards';
import QuizMe from './components/QuizMe';
import TeachDifferently from './components/TeachDifferently';
import GenerateSimilar from './components/GenerateSimilar';
import DownloadsBar from './components/DownloadsBar';

const legacyTabs = [
  { id: 'summary', label: 'Notes', icon: 'ri-file-list-line' },
  { id: 'flashcards', label: 'Cards', icon: 'ri-stack-line' },
  { id: 'quiz', label: 'Quiz', icon: 'ri-questionnaire-line' },
  { id: 'teach', label: 'Explain', icon: 'ri-refresh-line' },
  { id: 'similar', label: 'Similar', icon: 'ri-magic-line' },
];

const subjectNameMap: Record<string, { name: string; id: string }> = {
  'Calculus II': { name: 'Mathematics', id: 'math' },
  'Physics: Mechanics': { name: 'Physical Sciences', id: 'physics' },
  'Organic Chemistry': { name: 'Physical Sciences', id: 'physics' },
};

type SectionType = 'overview' | 'practice' | 'worksheets' | 'homework' | 'related' | 'legacy';

export default function LessonHub() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    headerBg: isDark ? 'bg-[#0F0F10]/80' : 'bg-white/80',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textMuted: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    divider: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    legacyTabBg: isDark ? 'bg-[#0F0F10]/90' : 'bg-white/90',
    backText: isDark ? 'text-white/30' : 'text-black/30',
    legacyTabActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
    legacyTabInactive: isDark ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08]' : 'bg-black/[0.04] text-black/40 hover:bg-black/[0.08]',
    backBtnBg: isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]',
    backBtnIcon: isDark ? 'text-white/40' : 'text-black/40',
  };

  const initialTab = location.state?.initialTab as string | undefined;
  const [activeSection, setActiveSection] = useState<SectionType>(
    initialTab && legacyTabs.find((t) => t.id === initialTab) ? 'legacy' : 'overview'
  );
  const [legacyTab, setLegacyTab] = useState(
    initialTab && legacyTabs.find((t) => t.id === initialTab) ? initialTab : 'summary'
  );

  const lesson = getLessonById(id || '');

  // ── History tracking ──
  const watchedSecondsRef = useRef(0);
  const isPlayingRef = useRef(false);
  const watchStartRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const topicState = location.state as {
    topicId?: string;
    topicName?: string;
    grade?: '10' | '11' | '12';
    subjectId?: string;
  } | undefined;

  const mappedSubject = lesson ? (subjectNameMap[lesson.subject] || { name: lesson.subject, id: 'math' }) : { name: 'Mathematics', id: 'math' };
  const resolvedSubjectId = topicState?.subjectId || mappedSubject.id;
  const resolvedSubjectName = topicState?.subjectId
    ? savedSubjects.find((s) => s.id === resolvedSubjectId)?.name || mappedSubject.name
    : mappedSubject.name;

  const handlePlayStateChange = (playing: boolean) => {
    isPlayingRef.current = playing;
    if (playing) {
      watchStartRef.current = Date.now();
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (isPlayingRef.current && watchStartRef.current) {
          const delta = Math.floor((Date.now() - watchStartRef.current) / 1000);
          if (delta > 0) {
            watchedSecondsRef.current += delta;
            watchStartRef.current = Date.now();
          }
        }
      }, 5000); // accumulate every 5s
    } else {
      if (watchStartRef.current) {
        const delta = Math.floor((Date.now() - watchStartRef.current) / 1000);
        if (delta > 0) watchedSecondsRef.current += delta;
        watchStartRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  useEffect(() => {
    const owner = getStudyScope();
    return () => {
      // Record any remaining watch time on unmount
      if (isPlayingRef.current && watchStartRef.current) {
        const delta = Math.floor((Date.now() - watchStartRef.current) / 1000);
        if (delta > 0) watchedSecondsRef.current += delta;
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (isStudyScopeCurrent(owner) && lesson && watchedSecondsRef.current > 0) {
        recordLessonWatch({
          id: lesson.id,
          title: lesson.title,
          subject: resolvedSubjectName,
          subjectId: resolvedSubjectId,
          topicId: topicState?.topicId || '',
          topicName: topicState?.topicName || '',
          grade: topicState?.grade || '10',
          thumbnail: lesson.videoThumbnail,
          duration: lesson.duration,
          watchedSeconds: Math.min(watchedSecondsRef.current, lesson.duration),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!lesson) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-full px-6 text-center ${t.pageBg} transition-colors duration-500`}>
        <motion.div
          className={`w-16 h-16 rounded-full ${isDark ? 'bg-white/[0.05]' : 'bg-black/[0.05]'} flex items-center justify-center mb-5`}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`ri-error-warning-line ${isDark ? 'text-white/30' : 'text-black/30'} text-xl`} />
          </div>
        </motion.div>
        <h2 className={`${t.textPrimary} text-base font-semibold mb-2`}>Lesson Not Found</h2>
        <p className={`${t.textMuted} text-xs mb-6 max-w-[260px] leading-relaxed`}>
          This lesson may have been removed or the link is incorrect.
        </p>
        <Pressable
          onClick={() => navigate('/history')}
          className={`px-6 py-3 ${isDark ? 'bg-white text-black' : 'bg-black text-white'} rounded-xl text-xs font-semibold`}
          scale={0.95}
        >
          Go to History
        </Pressable>
      </div>
    );
  }

  const handleSectionChange = (section: string) => {
    if (legacyTabs.find((t) => t.id === section)) {
      setLegacyTab(section);
      setActiveSection('legacy');
    } else {
      setActiveSection(section as SectionType);
    }
  };

  const handleBackToOverview = () => {
    setActiveSection('overview');
    setLegacyTab('summary');
  };

  return (
    <div className={`flex flex-col ${t.pageBg} min-h-0 transition-colors duration-500 lg:max-w-5xl lg:mx-auto lg:w-full`}>
      {/* Sticky Header */}
      <motion.div
        className={`flex-shrink-0 px-5 pt-5 pb-3 flex items-center gap-3 sticky top-0 z-30 ${t.headerBg} backdrop-blur-xl transition-colors duration-500`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
      >
        <Pressable
          onClick={() => navigate('/history')}
          className={`w-9 h-9 rounded-xl ${isDark ? 'bg-white/[0.05] hover:bg-white/[0.10]' : 'bg-black/[0.04] hover:bg-black/[0.08]'} flex items-center justify-center transition-colors`}
          scale={0.85}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-arrow-left-line ${t.textMuted} text-sm`} />
          </div>
        </Pressable>
        <div className="flex-1 min-w-0">
          <h1 className={`${t.textPrimary} text-sm font-semibold truncate`}>{lesson.title}</h1>
          <p className={`${t.textMuted} text-[11px]`}>{lesson.subject} &middot; {lesson.createdAt}</p>
        </div>
        <Pressable
          className={`w-9 h-9 rounded-xl ${isDark ? 'bg-white/[0.05] hover:bg-white/[0.10]' : 'bg-black/[0.04] hover:bg-black/[0.08]'} flex items-center justify-center transition-colors`}
          scale={0.85}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-more-2-line ${isDark ? 'text-white/40' : 'text-black/40'} text-sm`} />
          </div>
        </Pressable>
      </motion.div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0 no-scrollbar">
        {/* Video Player */}
        <div className="px-5 pb-2">
          <LessonPlayer lesson={lesson} onPlayStateChange={handlePlayStateChange} />
        </div>

        {/* Lesson Meta Bar */}
        <LessonMeta
          lesson={lesson}
          activeSection={activeSection === 'legacy' ? 'overview' : activeSection}
          onSectionChange={(section) => {
            if (section === 'overview') {
              setActiveSection('overview');
            } else {
              handleSectionChange(section);
            }
          }}
        />

        {/* Download Bar */}
        <DownloadsBar lessonTitle={lesson.title} />

        {/* Divider */}
        <div className={`mx-5 h-px ${t.divider} mb-1`} />

        {/* Legacy Tab Strip (only when in legacy mode) */}
        <AnimatePresence>
          {activeSection === 'legacy' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className={`sticky top-0 z-20 ${t.legacyTabBg} backdrop-blur-md pt-3 pb-2 px-5 transition-colors duration-500`}
            >
              <div className="flex items-center gap-2 mb-2">
                <motion.button
                  onClick={handleBackToOverview}
                  whileTap={{ scale: 0.9 }}
                  className={`w-7 h-7 rounded-lg ${t.backBtnBg} flex items-center justify-center cursor-pointer`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-left-line ${t.backBtnIcon} text-xs`} />
                  </div>
                </motion.button>
                <span className={`${t.backText} text-xs font-medium`}>Back to Overview</span>
              </div>
              <div className="flex gap-1 overflow-x-auto no-scrollbar pb-1">
                {legacyTabs.map((tab) => (
                  <motion.button
                    key={tab.id}
                    onClick={() => setLegacyTab(tab.id)}
                    whileTap={{ scale: 0.92 }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 cursor-pointer transition-all duration-200 ${
                      legacyTab === tab.id ? t.legacyTabActive : t.legacyTabInactive
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${tab.icon} text-[10px]`} />
                    </div>
                    {tab.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Section Content */}
        <div className="pt-4 pb-8">
          <AnimatePresence mode="wait">
            {activeSection === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                <AISummary lesson={lesson} />
                <LessonResources lesson={lesson} onNavigateTab={handleSectionChange} />
              </motion.div>
            )}

            {activeSection === 'practice' && (
              <motion.div
                key="practice"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                <PracticeTests lesson={lesson} />
              </motion.div>
            )}

            {activeSection === 'worksheets' && (
              <motion.div
                key="worksheets"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                <Worksheets lesson={lesson} />
              </motion.div>
            )}

            {activeSection === 'homework' && (
              <motion.div
                key="homework"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                <HomeworkHelp lesson={lesson} />
              </motion.div>
            )}

            {activeSection === 'related' && (
              <motion.div
                key="related"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                <RelatedVideos lesson={lesson} />
              </motion.div>
            )}

            {activeSection === 'legacy' && (
              <motion.div
                key="legacy"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              >
                {legacyTab === 'summary' && <SummaryNotes lesson={lesson} />}
                {legacyTab === 'flashcards' && <Flashcards cards={lesson.flashcards} />}
                {legacyTab === 'quiz' && <QuizMe questions={lesson.quiz} />}
                {legacyTab === 'teach' && <TeachDifferently styles={lesson.teachDifferently} />}
                {legacyTab === 'similar' && <GenerateSimilar lessons={lesson.similarLessons} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}